import { createAction, handleActions } from "redux-actions"
import { combineReducers } from "redux"
import { QUESTIONS } from "../../utils/calculator-data"
import { STATUS_CALCULATION_DONE } from "../../utils"

const nextQuestion = createAction("CALCULATOR_NEXT_QUESTION")
const previousQuestion = createAction("CALCULATOR_PREVIOUS_QUESTION")
const saveAnswer = createAction("CALCULATOR_SAVE_ANSWER")

// Helper function for calculating result
const calculateFootprint = (val, type, factor) => {
  switch (type) {
    case "radioEnergy":
    case "radio":
      return val
    case "inputNumber":
    case "inputNumberEuro":
      return val * factor
    case "housing":
      return (val.flatsize / val.flatmates) * val.heattype
    case "fixed":
      return val.value * factor
    default:
      break
  }
}

const findEquals = (array, value) => {
  return array.find((elem) => elem.value === value)
}

const findSmaller = (array, value) => {
  return array.find((elem) => {
    // if there is no value, it is the worst scenario (open end)
    if (elem.value) {
      return value < elem.value
    } else {
      return elem
    }
  })
}

const getTips = (reduction, value) => {
  const { algorithm, values } = reduction
  switch (algorithm) {
    case "smaller":
      return findSmaller(values, value)
    case "equals":
      return findEquals(values, value)
    case "housing":
      return findEquals(values, value.heattype)
    case "none":
      return null
    default:
      break
  }
}

// Actions
export const answerQuestion = (val, questionNumber, shouldContinue) => (
  dispatch
) => {
  // if user has decided
  if (shouldContinue) {
    dispatch(nextQuestion())
  } else {
    dispatch(saveAnswer({ values: val, questionNumber: questionNumber }))
  }
}

export const goBack = () => (dispatch) => {
  dispatch(previousQuestion())
}

// Reducers
const activeQuestion = handleActions(
  {
    [nextQuestion]: (state) => {
      if (state.number < QUESTIONS.length - 1) {
        return QUESTIONS[state.number + 1]
      } else {
        return STATUS_CALCULATION_DONE
      }
    },
    [previousQuestion]: (state) => {
      // if user finished calculation, go back to
      // last question
      if (state === STATUS_CALCULATION_DONE) {
        return QUESTIONS[QUESTIONS.length - 1]
      }
      // otherwise handle normally
      if (state.number > 0) {
        return QUESTIONS[state.number - 1]
      } else {
        return null
      }
    },
  },
  QUESTIONS[0]
)

const answers = handleActions(
  {
    [saveAnswer]: (state, { payload }) => {
      const newState = Object.assign(state, {})
      const allAnswers = newState.data
      allAnswers[payload.questionNumber] = payload

      let totalFootprint = 0
      // iterate through answers and calculate results
      Object.keys(allAnswers).forEach((answerKey) => {
        const answer = allAnswers[answerKey]
        const { type, factor, results, category } = QUESTIONS[
          answer.questionNumber
        ]
        // save the reduction tips in the answer result
        const reductionTips = getTips(results, answer.values)
        allAnswers[answerKey].tips = reductionTips
        // add category to show icon
        allAnswers[answerKey].category = category
        // calculate footprint
        totalFootprint += calculateFootprint(answer.values, type, factor)
      })

      return {
        total: totalFootprint,
        data: allAnswers,
      }
    },
  },
  { total: 0, data: {} }
)

// Combine reducers
export default combineReducers({
  activeQuestion,
  answers,
})

// Selectors
export const getActiveQuestion = (state) =>
  state.carbonCalculator.activeQuestion
export const getProgress = (state) => {
  const { activeQuestion } = state.carbonCalculator
  if (activeQuestion === STATUS_CALCULATION_DONE) {
    return 100
  } else {
    return activeQuestion
      ? (activeQuestion.number / QUESTIONS.length) * 100
      : 100
  }
}
export const getFootprint = (state) =>
  state.carbonCalculator.answers.total / 1000
export const getAllReductionTips = (state) => {
  const allAnswers = state.carbonCalculator.answers.data

  const tips = Object.keys(allAnswers)
    .filter((answerKey) => allAnswers[answerKey].tips)
    .map((answerKey) => {
      return {
        ...allAnswers[answerKey].tips,
        category: allAnswers[answerKey].category,
      }
    })
  return tips
}
export const getActiveAnswer = (state) => {
  if (state.carbonCalculator.activeQuestion) {
    const index = state.carbonCalculator.activeQuestion.number
    return state.carbonCalculator.answers.data[index]
  } else return null
}
